import { PushStationCredential, SnapshotStation } from "@/services/PushStationService";
import { Freshness, freshnessOf } from "./stationDisplay";

/**
 * SRF-2749 / SRF-2813 — the registry (Postgres) ⨝ Cloudflare snapshot join, shared by the
 * Cloudflare stations view and the station-management page so both resolve a station's
 * live values the same way.
 */

/** One row per physical station: a snapshot station, or a credential with no data yet. */
export interface StationRow {
  key: string;
  id: string;
  name: string;
  source: string;
  lat?: number | null;
  lon?: number | null;
  windKts?: number | null;
  gustKts?: number | null;
  windDir?: number | null;
  obsTs?: number | null;
  /** undefined = no observation at all (credential-only row). */
  stale?: boolean;
  freshness: Freshness;
  /** The registry row behind this station, when one exists. */
  credential?: PushStationCredential;
  /** True when the station is registered but absent from the snapshot (no observation yet). */
  noData?: boolean;
  /** Postgres station_id — the identity. Absent for snapshot-only provider stations. */
  registryId?: string;
  /** How observations arrive, independent of who registered it. */
  ingest: "push" | "pull";
  /** True when a station owner registered it themselves (vs. ops-created). */
  ownerRegistered: boolean;
}

export const isPullCredential = (c: PushStationCredential): boolean =>
  Boolean(c.contact?.pollUrl) || c.contact?.ingestMode === "pull";

/**
 * The snapshot id a registry station's observations are published under.
 *
 * The BACKEND resolves this and serves it as canonicalStationId. It used to be re-derived
 * here as `${provider}_${externalId}`, a rule that is right for Tempest and Pioupiou and
 * WRONG for clientraw: that format's external id is its poll URL, but ingest emits
 * `clientraw_<ourStationId>`. (Codex review round 4, P2-1.) The old derivation is kept only
 * as a fallback for a backend that does not send the field yet.
 */
export const canonicalIdOf = (c: PushStationCredential): string => {
  const provider = c.contact?.pollProvider?.trim();
  const external = c.contact?.pollExternalId?.trim();
  return (
    c.canonicalStationId?.trim() ||
    (isPullCredential(c) && provider && external ? `${provider}_${external}` : `push_${c.stationId}`)
  );
};

/** Snapshot lookup by id — build once, reuse for every credential. */
export const indexSnapshot = (snapshot: SnapshotStation[]): Map<string, SnapshotStation> =>
  new Map(snapshot.map((s) => [s.id, s]));

/**
 * ONE ROW PER PHYSICAL STATION (SRF-2813). The registry row is the identity; the snapshot
 * id is not (it encodes how the observation arrived), so keying on it would split one
 * station into a live pull row plus a push credential that can never have data.
 */
export function buildStationRows(
  snapshot: SnapshotStation[],
  credentials: PushStationCredential[]
): StationRow[] {
  const out: StationRow[] = [];
  const claimed = new Set<string>();
  const byId = indexSnapshot(snapshot);

  for (const c of credentials) {
    const isPull = isPullCredential(c);
    const provider = c.contact?.pollProvider?.trim();
    const canonicalId = canonicalIdOf(c);
    const snap = byId.get(canonicalId);
    if (snap) claimed.add(canonicalId);
    out.push({
      key: `station:${c.stationId}`,
      id: canonicalId,
      name: c.name || c.stationId,
      // The badge shows how data actually arrives, so a pull station reads "tempest",
      // not "push", even though its registry row lives in live_wind_push_station.
      source: snap?.source ?? (isPull && provider ? provider : "push"),
      lat: snap?.lat ?? c.lat,
      lon: snap?.lon ?? c.lon,
      windKts: snap?.windKts,
      gustKts: snap?.gustKts,
      windDir: snap?.windDir,
      obsTs: snap?.obsTs,
      stale: snap?.stale,
      freshness: freshnessOf(snap?.stale, !snap),
      credential: c,
      noData: !snap,
      registryId: c.stationId,
      ingest: isPull ? "pull" : "push",
      ownerRegistered: c.contact?.ownerUserId != null,
    });
  }

  // Snapshot stations with no registry row: the scraped providers we operate ourselves
  // (metar/ndbc/knmi/pws) and any push station whose credential is missing.
  for (const s of snapshot) {
    if (claimed.has(s.id)) continue;
    out.push({
      key: `snap:${s.id}`,
      id: s.id,
      name: s.name,
      source: s.source,
      lat: s.lat,
      lon: s.lon,
      windKts: s.windKts,
      gustKts: s.gustKts,
      windDir: s.windDir,
      obsTs: s.obsTs,
      stale: s.stale,
      freshness: freshnessOf(s.stale),
      ingest: s.source === "push" ? "push" : "pull",
      ownerRegistered: false,
    });
  }
  return out;
}

/**
 * Credential state of a row, as one filterable key (SRF-2813). A pull station's data does
 * not arrive through a credential, so it reports "pull" rather than its (unused) push
 * credential's status.
 */
export type CredentialKey = "active" | "pending" | "revoked" | "pull" | "none";
export const credentialKeyOf = (row: StationRow): CredentialKey => {
  const cred = row.credential;
  if (!cred) return "none";
  if (row.ingest === "pull") return "pull";
  if (!cred.prefix) return "none";
  return cred.status;
};
